import {
    Add,
    AttachFile,
    Close,
    Delete,
    GetApp,
    MoreVert,
    Search,
    Send
} from "@mui/icons-material";
import {
    Badge,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Loading } from "@sinossi/mates-react-library";
import React from "react";

import { Trans } from "@lingui/macro";
import { PageHeader, RoutesData } from "components/commons";
import { MatesComponent, MatesComponentState } from "components/mates-component";
import { FormattedMessageContent } from "components/messages/formatted-message-content";
import { CustomDropzoneUploadDocument } from "components/brand/customDropzoneUploadDocument";
import {
    Conversation,
    ConversationFilter,
    CreateConversationRequest,
    Message,
    MessageAttachment,
    MessageFilter,
    SendMessageRequest,
    SessionResponse
} from "models";
import { MessageService } from "services/message.service";
import withUseRouter, { UseRouterParams } from "services/router";

import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { convertFromRaw, convertToRaw, EditorState } from "draft-js";

const ConversationListContainer = styled(Paper)(({ theme }) => ({
    height: "calc(100vh - 200px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
}));

const MessageContainer = styled(Paper)(({ theme }) => ({
    height: "calc(100vh - 200px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
}));

const ConversationList = styled(List)(({ theme }) => ({
    flex: 1,
    overflow: "auto",
    padding: 0,
    "& .MuiListItemButton-root": {
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
        },
        "&.selected": {
            backgroundColor: theme.palette.action.selected,
        },
    },
}));

const MessageList = styled(Box)(({ theme }) => ({
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
}));

const MessageBubble = styled(Box)<{ isOwn: boolean }>(({ theme, isOwn }) => ({
    maxWidth: "70%",
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(2),
    backgroundColor: isOwn ? theme.palette.primary.main : theme.palette.grey[200],
    color: isOwn ? theme.palette.primary.contrastText : theme.palette.text.primary,
    alignSelf: isOwn ? "flex-end" : "flex-start",
    wordBreak: "break-word",
    display: "flex",
    flexDirection: "column",
}));

const MessageSender = styled(Typography)<{ isOwn: boolean }>(({ theme, isOwn }) => ({
    fontSize: "0.75rem",
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: isOwn ? theme.palette.primary.contrastText : theme.palette.text.secondary,
    opacity: 0.8,
}));

const MessageInput = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-end",
}));

interface MessagesProps extends UseRouterParams {
    sessionData: SessionResponse;
}

interface MessagesState extends MatesComponentState {
    conversations: Conversation[];
    selectedConversation: Conversation | null;
    messages: Message[];
    newMessageEditorState: EditorState;
    searchTerm: string;
    showNewConversationDialog: boolean;
    newConversationSubject: string;
    newConversationMessageEditorState: EditorState;
    conversationFilter: ConversationFilter;
    messageFilter: MessageFilter;
    availableRecipients: any[];
    selectedRecipients: string[];
    recipientSearchTerm: string;
    hasSearchedRecipients: boolean;
    // Stato per gli allegati
    selectedAttachments: File[];
    showAttachmentDialog: boolean;
    selectedFile: File | null;
    // Stato menu MoreVert
    moreMenuAnchorEl: HTMLElement | null;
    showDeleteConversationDialog: boolean;
}

class Messages extends React.Component<MessagesProps, MessagesState> {
    private readonly matesComponent: MatesComponent<MessagesProps, MessagesState> = new MatesComponent(this);

    constructor(props: MessagesProps) {
        super(props);
        this.state = {
            ...this.matesComponent.setupState(),
            conversations: [],
            selectedConversation: null,
            messages: [],
            newMessageEditorState: EditorState.createEmpty(),
            searchTerm: "",
            showNewConversationDialog: false,
            newConversationSubject: "",
            newConversationMessageEditorState: EditorState.createEmpty(),
            conversationFilter: {
                page: 0,
                size: 50,
                sortBy: "lastMessageAt",
                sortDirection: "DESC",
                isArchived: false,
            },
            messageFilter: {
                page: 0,
                size: 50,
                sortBy: "sentAt",
                sortDirection: "ASC",
            },
            availableRecipients: [],
            selectedRecipients: [],
            recipientSearchTerm: "",
            hasSearchedRecipients: false,
            // Inizializzazione stato allegati
            selectedAttachments: [],
            showAttachmentDialog: false,
            selectedFile: null,
            // Inizializzazione menu MoreVert
            moreMenuAnchorEl: null,
            showDeleteConversationDialog: false,
        };
    }

    componentDidMount(): void {
        this.loadConversations();
        this.handleConversationFromQuery(this.props.searchParams.get("conversation"));
    }

    componentDidUpdate(prevProps: MessagesProps): void {
        if (prevProps.location.search !== this.props.location.search) {
            this.handleConversationFromQuery(this.props.searchParams.get("conversation"));
        }
    }

    private handleConversationFromQuery = (conversationId: string | null): void => {
        if (!conversationId) {
            return;
        }

        if (this.state.selectedConversation?.id === conversationId) {
            return;
        }

        this.loadConversationById(conversationId);
    };

    private loadConversations = (): void => {
        this.setState({ loading: true });
        MessageService.getUserConversations(
            this.state.conversationFilter,
            this.loadConversationsCallback,
            (error: any) => {
                console.error('Errore nel caricamento delle conversazioni:', error);
                this.setState({
                    loading: false,
                    conversations: []
                });
                this.matesComponent.onException(error);
            }
        );
    };

    private loadConversationsCallback = (response: any): void => {
        console.log(response)
        this.setState({
            loading: false,
            conversations: response && response.data.items && Array.isArray(response.data.items) ? response.data.items : [],
        });
    };

    private loadConversationById = (conversationId: string): void => {
        MessageService.getConversation(
            conversationId,
            (response: any) => {
                const conversation: Conversation | undefined = response?.data ?? response;

                if (!conversation) {
                    return;
                }

                this.setState({ selectedConversation: conversation });
                this.loadMessages(conversationId);
                this.markAsRead(conversationId);
            },
            this.matesComponent.onException
        );
    };

    private selectConversation = (conversation: Conversation): void => {
        this.setState({ selectedConversation: conversation });
        this.loadMessages(conversation.id);
        this.markAsRead(conversation.id);
    };

    private loadMessages = (conversationId: string): void => {
        MessageService.getConversationMessages(
            conversationId,
            this.state.messageFilter,
            this.loadMessagesCallback,
            (error: any) => {
                console.error('Errore nel caricamento dei messaggi:', error);
                this.setState({ messages: [] });
                this.matesComponent.onException(error);
            }
        );
    };

    private loadMessagesCallback = (response: any): void => {
        this.setState({
            messages: response && response.data.items && Array.isArray(response.data.items) ? response.data.items : []
        });
    };

    private markAsRead = (conversationId: string): void => {
        MessageService.markConversationAsRead(
            conversationId,
            () => {
                this.loadConversations();
            },
            this.matesComponent.onException
        );
    };

    private sendMessage = (): void => {
        if (!this.state.selectedConversation) {
            console.error('Nessuna conversazione selezionata');
            return;
        }

        if (!this.state.selectedConversation.id) {
            console.error('ID conversazione mancante:', this.state.selectedConversation);
            return;
        }

        const contentState = this.state.newMessageEditorState.getCurrentContent();
        const hasText = contentState.hasText();
        const hasAttachments = this.state.selectedAttachments.length > 0;
        
        if (!hasText && !hasAttachments) {
            return;
        }

        const request: SendMessageRequest = {
            conversationId: this.state.selectedConversation.id,
            content: convertToRaw(contentState),
        };

        console.log('Invio messaggio con request:', request);

        MessageService.sendMessageWithAttachment(
            request,
            this.state.selectedAttachments[0] || null, // Per ora supportiamo un solo allegato
            () => {
                this.setState({
                    newMessageEditorState: EditorState.createEmpty(),
                    selectedAttachments: [] // Pulisce gli allegati dopo l'invio
                });
                this.loadMessages(this.state.selectedConversation!.id);
                this.loadConversations(); // Aggiorna la lista per l'ultimo messaggio
            },
            (error: any) => {
                console.error('Errore nell\'invio del messaggio:', error);
                this.matesComponent.onException(error);
            }
        );
    };

    private handleNewMessageEditorChange = (editorState: EditorState): void => {
        this.setState({ newMessageEditorState: editorState });
    };

    private handleNewConversationMessageEditorChange = (editorState: EditorState): void => {
        this.setState({ newConversationMessageEditorState: editorState });
    };

    private handleKeyPress = (event: React.KeyboardEvent): void => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    };

    private openNewConversationDialog = (): void => {
        this.setState({
            showNewConversationDialog: true,
            availableRecipients: [],
            selectedRecipients: [],
            recipientSearchTerm: "",
            hasSearchedRecipients: false,
            loading: false
        });
    };

    private closeNewConversationDialog = (): void => {
        this.setState({
            showNewConversationDialog: false,
            newConversationSubject: "",
            newConversationMessageEditorState: EditorState.createEmpty(),
            selectedRecipients: [],
            recipientSearchTerm: "",
            availableRecipients: [],
            hasSearchedRecipients: false,
            loading: false
        });
    };

    private loadAvailableRecipients = (): void => {
        console.log('Caricamento destinatari disponibili...');

        MessageService.getAvailableRecipients(
            (response: any) => {
                console.log('Risposta API ricevuta:', response);

                let recipients = [];

                // Gestione robusta della risposta
                if (response) {
                    recipients = response.data.items;
                }

                console.log('Destinatari processati:', recipients);

                this.setState({
                    availableRecipients: recipients,
                    loading: false
                });
            },
            (error: any) => {
                console.error('Errore nel caricamento degli utenti disponibili:', error);
                this.setState({
                    availableRecipients: [],
                    loading: false
                });
            },
            0,
            50,
            this.state.recipientSearchTerm
        );
    };

    private toggleRecipientSelection = (recipientId: string): void => {
        const { selectedRecipients } = this.state;
        if (selectedRecipients.includes(recipientId)) {
            this.setState({
                selectedRecipients: selectedRecipients.filter(id => id !== recipientId)
            });
        } else {
            this.setState({
                selectedRecipients: [...selectedRecipients, recipientId]
            });
        }
    };

    private searchRecipients = (): void => {
        // Verifica che ci sia un termine di ricerca
        if (!this.state.recipientSearchTerm.trim()) {
            return;
        }
        
        this.setState({
            loading: true,
            hasSearchedRecipients: true
        });
        this.loadAvailableRecipients();
    };

    private createNewConversation = (): void => {
        if (this.state.selectedRecipients.length === 0) {
            // Mostra errore se nessun destinatario è selezionato
            alert("Seleziona almeno un destinatario per la conversazione");
            return;
        }

        const contentState = this.state.newConversationMessageEditorState.getCurrentContent();
        const initialMessage = contentState.hasText() ?
            contentState.getPlainText() : undefined;

        const request: CreateConversationRequest = {
            subject: this.state.newConversationSubject,
            participantIds: this.state.selectedRecipients,
            initialMessage: initialMessage,
        };

        MessageService.createConversation(
            request,
            () => {
                this.closeNewConversationDialog();
                this.loadConversations();
            },
            this.matesComponent.onException
        );
    };

    private searchConversations = (): void => {
        if (!this.state.searchTerm.trim()) {
            this.loadConversations();
            return;
        }

        MessageService.searchConversations(
            this.state.searchTerm,
            this.state.conversationFilter,
            this.loadConversationsCallback,
            (error: any) => {
                console.error('Errore nella ricerca delle conversazioni:', error);
                this.setState({ conversations: [] });
                this.matesComponent.onException(error);
            }
        );
    };

    private formatMessageTime = (timestamp: string): string => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

        if (diffInHours < 24) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else {
            return date.toLocaleDateString();
        }
    };

    // Metodi per gestire gli allegati
    private openAttachmentDialog = (): void => {
        this.setState({ showAttachmentDialog: true });
    };

    private closeAttachmentDialog = (): void => {
        this.setState({
            showAttachmentDialog: false,
            selectedFile: null
        });
    };

    private handleFileSelection = (file: File): void => {
        this.setState({ selectedFile: file });
    };

    private confirmFileSelection = (): void => {
        if (!this.state.selectedFile) {
            return;
        }

        this.setState({
            selectedAttachments: [...this.state.selectedAttachments, this.state.selectedFile],
            showAttachmentDialog: false,
            selectedFile: null
        });
    };

    private removeAttachment = (index: number): void => {
        this.setState({
            selectedAttachments: this.state.selectedAttachments.filter((_, i) => i !== index)
        });
    };

    private downloadAttachment = (attachment: MessageAttachment): void => {
        MessageService.downloadAttachment(attachment.id, attachment.fileName);
    };

    private openMoreMenu = (event: React.MouseEvent<HTMLElement>): void => {
        this.setState({ moreMenuAnchorEl: event.currentTarget });
    };

    private closeMoreMenu = (): void => {
        this.setState({ moreMenuAnchorEl: null });
    };

    private handleDeleteConversationConfirm = (): void => {
        const conversationId = this.state.selectedConversation?.id;
        if (!conversationId) return;

        this.setState({ loading: true, showDeleteConversationDialog: false, moreMenuAnchorEl: null });
        MessageService.deleteConversationForUser(
            conversationId,
            () => {
                this.setState({ loading: false, selectedConversation: null, messages: [] });
                this.loadConversations();
                this.props.navigate(RoutesData.MESSAGES.path);
            },
            (error: any) => {
                this.setState({ loading: false });
                this.matesComponent.onException(error);
            }
        );
    };

    private formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    private isCurrentUser = (senderId: string): boolean => {
        return senderId === this.props.sessionData?.accountDetails?.account?.id;
    };

    private getSenderName = (message: Message): string => {
        if (!message.sender) return 'Utente sconosciuto';
        
        const { accountDetails } = message.sender;
        if (accountDetails?.firstName && accountDetails?.lastName) {
            return `${accountDetails.firstName} ${accountDetails.lastName}`;
        }
        
        return message.sender.username || 'Utente sconosciuto';
    };

    private shouldShowUnreadBadge = (conversation: Conversation): boolean => {
        // Mostra il badge solo se ci sono messaggi non letti E l'ultimo messaggio non è dell'utente corrente
        if (!conversation.unreadCount || conversation.unreadCount === 0) {
            return false;
        }
        
        // Se non c'è un ultimo messaggio, mostra il badge
        if (!conversation.lastMessage?.sender?.id) {
            return true;
        }
        
        // Mostra il badge solo se l'ultimo messaggio non è dell'utente corrente
        return !this.isCurrentUser(conversation.lastMessage.sender.id);
    };

    render() {
        return (
            <Box sx={{ width: "100%", pt: 2, position: "relative" }}>
                <Loading show={this.state.loading} />
                <PageHeader
                    title={RoutesData.MESSAGES.title}
                    paths={[RoutesData.DASHBOARD, RoutesData.MESSAGES]}
                />

                <Box sx={{ mt: 3 }}>
                    <Grid container spacing={2} sx={{ height: "calc(100vh - 200px)" }}>
                        {/* Lista conversazioni */}
                        <Grid item xs={12} md={4}>
                            <ConversationListContainer>
                                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Cerca conversazioni..."
                                            value={this.state.searchTerm}
                                            onChange={(e) => this.setState({ searchTerm: e.target.value })}
                                            onKeyPress={(e) => e.key === "Enter" && this.searchConversations()}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<Add />}
                                            onClick={this.openNewConversationDialog}
                                        >
                                            Nuovo
                                        </Button>
                                    </Box>
                                </Box>

                                <ConversationList>
                                    {(this.state.conversations || []).map((conversation) => (
                                        <ListItemButton
                                            key={conversation.id}
                                            className={
                                                this.state.selectedConversation?.id === conversation.id
                                                    ? "selected"
                                                    : ""
                                            }
                                            onClick={() => this.selectConversation(conversation)}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                            {conversation.subject}
                                                        </Typography>
                                                        {this.shouldShowUnreadBadge(conversation) && (
                                                            <Badge badgeContent={conversation.unreadCount} color="error" />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Box
                                                            sx={{
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {conversation.lastMessage?.content ? (
                                                                <FormattedMessageContent
                                                                    content={conversation.lastMessage.content}
                                                                    variant="body2"
                                                                    maxLines={1}
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Nessun messaggio
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {this.formatMessageTime(conversation.lastMessageAt)}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </ListItemButton>
                                    ))}
                                </ConversationList>
                            </ConversationListContainer>
                        </Grid>

                        {/* Area messaggi */}
                        <Grid item xs={12} md={8}>
                            <MessageContainer>
                                {this.state.selectedConversation ? (
                                    <>
                                        {/* Header conversazione */}
                                        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <Box>
                                                    <Typography variant="h6">
                                                        {this.state.selectedConversation.subject}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {this.state.selectedConversation.participants && Array.isArray(this.state.selectedConversation.participants)
                                                            ? this.state.selectedConversation.participants
                                                                .map((p) => {
                                                                    if (p.account?.accountDetails?.firstName && p.account?.accountDetails?.lastName) {
                                                                        return `${p.account.accountDetails.firstName} ${p.account.accountDetails.lastName}`;
                                                                    } else if (p.account?.username) {
                                                                        return p.account.username;
                                                                    } else {
                                                                        return 'Utente sconosciuto';
                                                                    }
                                                                })
                                                                .join(", ")
                                                            : 'Nessun partecipante'}
                                                    </Typography>
                                                </Box>
                                                <IconButton onClick={this.openMoreMenu}>
                                                    <MoreVert />
                                                </IconButton>
                                                <Menu
                                                    anchorEl={this.state.moreMenuAnchorEl}
                                                    open={Boolean(this.state.moreMenuAnchorEl)}
                                                    onClose={this.closeMoreMenu}
                                                >
                                                    <MenuItem
                                                        onClick={() => this.setState({ showDeleteConversationDialog: true, moreMenuAnchorEl: null })}
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        <Trans id="messages.delete-conversation">Elimina conversazione</Trans>
                                                    </MenuItem>
                                                </Menu>
                                            </Box>
                                        </Box>

                                        {/* Lista messaggi */}
                                        <MessageList>
                                            {(this.state.messages || []).map((message) => {
                                                const isOwn = this.isCurrentUser(message.sender?.id || '');
                                                const senderName = this.getSenderName(message);
                                                
                                                return (
                                                    <MessageBubble
                                                        key={message.id}
                                                        isOwn={isOwn}
                                                    >
                                                        <MessageSender isOwn={isOwn}>
                                                            {senderName}
                                                        </MessageSender>
                                                        <FormattedMessageContent
                                                            content={message.content}
                                                            variant="body2"
                                                            attachments={message.attachments}
                                                            onDownloadAttachment={this.downloadAttachment}
                                                        />
                                                        <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5, display: "block" }}>
                                                            {this.formatMessageTime(message.sentAt)}
                                                        </Typography>
                                                    </MessageBubble>
                                                );
                                            })}
                                        </MessageList>

                                        {/* Input messaggio */}
                                        <MessageInput>
                                            <Box sx={{ flex: 1 }}>
                                                {/* Visualizzazione allegati selezionati */}
                                                {this.state.selectedAttachments.length > 0 && (
                                                    <Box sx={{ mb: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                                            Allegati ({this.state.selectedAttachments.length}):
                                                        </Typography>
                                                        {this.state.selectedAttachments.map((file, index) => (
                                                            <Chip
                                                                key={index}
                                                                label={`${file.name} (${this.formatFileSize(file.size)})`}
                                                                onDelete={() => this.removeAttachment(index)}
                                                                deleteIcon={<Delete />}
                                                                size="small"
                                                                sx={{ mr: 1, mb: 0.5 }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                                
                                                <Editor
                                                    editorState={this.state.newMessageEditorState}
                                                    onEditorStateChange={this.handleNewMessageEditorChange}
                                                    toolbar={{
                                                        options: ['inline', 'list', 'link'],
                                                        inline: {
                                                            options: ['bold', 'italic'],
                                                        },
                                                        list: {
                                                            options: ['unordered', 'ordered'],
                                                        },
                                                        link: {
                                                            options: ['link'],
                                                        },
                                                    }}
                                                    toolbarStyle={{
                                                        backgroundColor: "#11347B",
                                                        borderColor: "#11347B"
                                                    }}
                                                    editorStyle={{
                                                        minHeight: "60px",
                                                        maxHeight: "120px",
                                                        border: "1px solid lightgray",
                                                        overflow: "auto",
                                                        padding: "8px",
                                                    }}
                                                    placeholder="Scrivi un messaggio..."
                                                />
                                            </Box>
                                            <IconButton onClick={this.openAttachmentDialog}>
                                                <AttachFile />
                                            </IconButton>
                                            <IconButton
                                                color="primary"
                                                onClick={this.sendMessage}
                                                disabled={!this.state.newMessageEditorState.getCurrentContent().hasText() && this.state.selectedAttachments.length === 0}
                                            >
                                                <Send />
                                            </IconButton>
                                        </MessageInput>
                                    </>
                                ) : (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: "100%",
                                            color: "text.secondary",
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ mb: 1 }}>
                                        
                                                Seleziona una conversazione
                                        </Typography>
                                        <Typography variant="body2">
                                                Scegli una conversazione dalla lista per iniziare a chattare
                                        </Typography>
                                    </Box>
                                )}
                            </MessageContainer>
                        </Grid>
                    </Grid>
                </Box>

                {/* Dialog nuova conversazione */}
                <Dialog
                    open={this.state.showNewConversationDialog}
                    onClose={this.closeNewConversationDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Trans id="messages.new-conversation">Nuova Conversazione</Trans>
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            label="Oggetto"
                            value={this.state.newConversationSubject}
                            onChange={(e) => this.setState({ newConversationSubject: e.target.value })}
                            sx={{ mb: 2, mt: 1 }}
                        />

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Destinatari:
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Cerca utenti..."
                                value={this.state.recipientSearchTerm}
                                onChange={(e) => this.setState({ recipientSearchTerm: e.target.value })}
                                onKeyPress={(e) => e.key === "Enter" && this.searchRecipients()}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                variant="outlined"
                                onClick={this.searchRecipients}
                                disabled={this.state.loading || !this.state.recipientSearchTerm.trim()}
                            >
                                Cerca
                            </Button>
                        </Box>

                        <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, mb: 2, position: 'relative' }}>
                            {this.state.loading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                                    <Loading show={true} />
                                </Box>
                            )}

                            {!this.state.loading && !this.state.hasSearchedRecipients && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100, textAlign: 'center', p: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Inserisci un termine di ricerca e premi "Cerca" per trovare utenti disponibili
                                    </Typography>
                                </Box>
                            )}

                            {!this.state.loading && this.state.hasSearchedRecipients && (
                                <List dense>
                                    {this.state.availableRecipients && this.state.availableRecipients.length > 0 ? (
                                        this.state.availableRecipients.map((recipient) => (
                                            <ListItem key={recipient.id} dense>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={this.state.selectedRecipients.includes(recipient.id)}
                                                            onChange={() => this.toggleRecipientSelection(recipient.id)}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {recipient.accountDetails?.firstName && recipient.accountDetails?.lastName
                                                                    ? `${recipient.accountDetails.firstName} ${recipient.accountDetails.lastName}`
                                                                    : recipient.username || 'Utente sconosciuto'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {recipient.accountType} - {recipient.username}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                        ))
                                    ) : (
                                        <ListItem>
                                            <Typography variant="body2" color="text.secondary">
                                                Nessun utente trovato per la ricerca "{this.state.recipientSearchTerm}"
                                            </Typography>
                                        </ListItem>
                                    )}
                                </List>
                            )}
                        </Box>

                        {this.state.selectedRecipients.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Destinatari selezionati: {this.state.selectedRecipients.length}
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    {this.state.selectedRecipients.map(recipientId => {
                                        const recipient = this.state.availableRecipients.find(r => r.id === recipientId);
                                        return recipient ? (
                                            <Chip
                                                key={recipientId}
                                                label={recipient.accountDetails?.firstName && recipient.accountDetails?.lastName
                                                    ? `${recipient.accountDetails.firstName} ${recipient.accountDetails.lastName}`
                                                    : recipient.username}
                                                onDelete={() => this.toggleRecipientSelection(recipientId)}
                                                size="small"
                                                sx={{ mr: 1, mb: 1 }}
                                            />
                                        ) : null;
                                    })}
                                </Box>
                            </Box>
                        )}

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Messaggio iniziale:
                            </Typography>
                            <Editor
                                editorState={this.state.newConversationMessageEditorState}
                                onEditorStateChange={this.handleNewConversationMessageEditorChange}
                                toolbar={{
                                    options: ['inline', 'list', 'link'],
                                    inline: {
                                        options: ['bold', 'italic'],
                                    },
                                    list: {
                                        options: ['unordered', 'ordered'],
                                    },
                                    link: {
                                        options: ['link'],
                                    },
                                }}
                                toolbarStyle={{
                                    backgroundColor: "#11347B",
                                    borderColor: "#11347B"
                                }}
                                editorStyle={{
                                    minHeight: "100px",
                                    maxHeight: "200px",
                                    border: "1px solid lightgray",
                                    overflow: "auto",
                                    padding: "8px",
                                }}
                                placeholder="Scrivi il messaggio iniziale..."
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeNewConversationDialog}>
                            <Trans id="common.cancel">Annulla</Trans>
                        </Button>
                        <Button
                            onClick={this.createNewConversation}
                            variant="contained"
                            disabled={!this.state.newConversationSubject.trim() || this.state.selectedRecipients.length === 0}
                        >
                            <Trans id="messages.create-conversation">Crea Conversazione</Trans>
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog upload allegati */}
                <Dialog
                    open={this.state.showAttachmentDialog}
                    onClose={this.closeAttachmentDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Trans id="messages.upload-attachment">Carica Allegato</Trans>
                    </DialogTitle>
                    <DialogContent>
                        <CustomDropzoneUploadDocument
                            callback={this.handleFileSelection}
                            text="Trascina o clicca per selezionare un file"
                        />
                        {this.state.selectedFile && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                <Typography variant="body2">
                                    File selezionato: <strong>{this.state.selectedFile.name}</strong>
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Dimensione: {this.formatFileSize(this.state.selectedFile.size)}
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeAttachmentDialog}>
                            <Trans id="common.cancel">Annulla</Trans>
                        </Button>
                        <Button
                            onClick={this.confirmFileSelection}
                            variant="contained"
                            disabled={!this.state.selectedFile}
                        >
                            <Trans id="messages.add-attachment">Aggiungi Allegato</Trans>
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog conferma eliminazione conversazione */}
                <Dialog
                    open={this.state.showDeleteConversationDialog}
                    onClose={() => this.setState({ showDeleteConversationDialog: false })}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>
                        <Trans id="messages.delete-conversation-title">Elimina conversazione</Trans>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">
                            <Trans id="messages.delete-conversation-body">
                                Sei sicuro di voler eliminare questa conversazione? L'azione è irreversibile per il tuo account. Gli altri partecipanti potranno ancora visualizzarla.
                            </Trans>
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => this.setState({ showDeleteConversationDialog: false })}>
                            <Trans id="common.cancel">Annulla</Trans>
                        </Button>
                        <Button
                            onClick={this.handleDeleteConversationConfirm}
                            variant="contained"
                            color="error"
                        >
                            <Trans id="messages.delete-conversation-confirm">Elimina</Trans>
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }
}

export default withUseRouter(Messages);
